// Surplus bundles (2026-10-26): laser pieces nobody ordered get a 4x6 label on the laser Zebra at Complete and a
// "Labeled + racked" click on the laser packaging screen. API: jobq-api main.py "SURPLUS BUNDLES".
// Surplus rack (same day, step 2): OWED bundles too (pieces an order still needs, labelled and racked the same way),
// and PULLS: pieces promised to an order's pallet count there only once someone moves them and says so (Mike:
// "only when moved"). API: jobq-api main.py "SURPLUS RACK"; page: pages/SurplusRackPage.jsx.
// Surplus crates (2026-11-03): ONE part per Uline mesh crate, a pallet beacon on each. A bundle's `crate` (the API's
// view: beacon_id, new, in_crate, status) says which crate its pieces go in; In crate = a tick, Other crate = a typed
// beacon number (a free one starts a new crate). API: jobq-api main.py "SURPLUS CRATES".
import config from "../../config";

const BASE = () => config.API_BASE_URL;

async function get(path) {
  const res = await fetch(`${BASE()}${path}`);
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const detail = data && data.detail;
    throw new Error(typeof detail === "string" ? detail : `Server answered ${res.status}`);
  }
  return data;
}

/** A refusal the caller may override (409 "no room": move anyway). */
export class ConflictError extends Error {}

async function post(path, body) {
  const res = await fetch(`${BASE()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const detail = data && data.detail;
    const msg = typeof detail === "string" ? detail
      : (detail && typeof detail.message === "string" ? detail.message : `Server answered ${res.status}`);
    const err = res.status === 409 ? new ConflictError(msg) : new Error(msg);
    if (detail && typeof detail === "object") {
      err.code = detail.error;          // e.g. second_crate, no_free_beacon, other_part
      err.detail = detail;
    }
    throw err;
  }
  return data;
}

/** The rack: {bundles, pulls, set_up}. */
export function loadRack({ includeClosed = false } = {}) {
  return get(`/surplus_rack${includeClosed ? "?include_closed=true" : ""}`);
}

/** One bundle with its pulls and audit trail. */
export function loadBundle(bundleId) {
  return get(`/surplus_bundles/${bundleId}`);
}

/** Allocate `qty` pieces of a surplus bundle to an order line. Resolves {allocations, pulls, bundle}. */
export function allocateBundle(bundleId, pin, { target_so_id, target_so_line_id, quantity }) {
  return post(`/surplus_bundles/${bundleId}/allocate`, { pin, target_so_id, target_so_line_id, qty: quantity });
}

/** Where a pull's pieces would land now (nothing written). */
export function previewPull(pullId) {
  return get(`/surplus_pulls/${pullId}/preview`);
}

/** The pieces are on the order's pallet (PIN). force = move even when the plan has no room for all of them. */
export function movePull(pullId, pin, force = false) {
  return post(`/surplus_pulls/${pullId}/moved`, { pin, force });
}

/** Undo a pull still on the rack (cancels its allocation; an owed one becomes surplus). */
export function cancelPull(pullId, pin) {
  return post(`/surplus_pulls/${pullId}/cancel`, { pin });
}

export function scrapBundle(bundleId, pin, qty, note) {
  return post(`/surplus_bundles/${bundleId}/scrap`, { pin, qty, note });
}

export function stockBundle(bundleId, pin, undo = false) {
  return post(`/surplus_bundles/${bundleId}/stock`, { pin, undo });
}

/** Lines of an order that pieces on the rack could fill: {matches: [...]}. */
export function rackMatches(soId) {
  return get(`/surplus_rack/matches?so_id=${encodeURIComponent(soId)}`);
}

/** "OWED → SO-1144", "STOCK", "SURPLUS": what a bundle is, for its badge. */
export function bundleKindText(b) {
  if (!b) return "";
  if (b.kind === "owed") return `OWED → ${b.owed_order_number || "its order"}`;
  if (b.stock) return "STOCK";
  return "SURPLUS";
}

/** "8 on PLT-AB12 · 2 on PLT-CD34" from a move's (or preview's) landed pallets; "" when none. */
export function landedText(landed) {
  const list = Array.isArray(landed) ? landed.filter((x) => x && x.qty > 0) : [];
  return list.map((x) => `${x.qty} on ${x.short_id || "a box with no pallet"}`).join(" · ");
}

/** The words for a pull: "10 pcs FAB311160 from #12 → SO-1144". */
export function pullLine(p) {
  return `${p.qty} pcs ${p.product_id || ""} from #${p.bundle_id} → ${p.target_order_number || "its order"}`.replace("  ", " ");
}

/** Record that the bundle is labeled and on the surplus rack. Resolves {bundle, already}. */
export function rackBundle(bundleId, pin) {
  return post(`/surplus_bundles/${bundleId}/racked`, { pin });
}

/** Put a bundle in a surplus crate and record it racked (PIN). beacon null = its own crate (a tick); a beacon number
 *  (BCN-031, 31) = that crate, or a free beacon for a new crate. secondCrate: start a second crate for a part that has
 *  one (the 409 `second_crate` asks first). Resolves {crate, beacon_id, new_crate, released_beacon_id, bundle}. */
export function putInCrate(bundleId, pin, { beacon = null, secondCrate = false, method = null } = {}) {
  return post(`/surplus_bundles/${bundleId}/crate`, {
    pin, beacon: beacon || null, second_crate: !!secondCrate, ...(method ? { method } : {}),
  });
}

/** Light a crate's beacon ("come find this crate"). Resolves POST /beacons/{id}/find's answer. */
export function findCrate(crateId) {
  return post(`/surplus_crates/${crateId}/find`, { actor: "surplus rack" });
}

/** Stop a crate's light now. */
export function crateLightsOff(crateId) {
  return post(`/surplus_crates/${crateId}/lights_off`);
}

/** A bundle's crate when it is open (else null): the API's `crate`. */
export function openCrate(b) {
  const c = b && b.crate;
  return c && c.status === "open" && c.beacon_id ? c : null;
}

/** The open crates of a part (GET /surplus_crates?part_id=): the "Use existing crate" box offers them. Resolves
 *  {crates, on}. */
export function loadPartCrates(partId) {
  return get(`/surplus_crates?part_id=${encodeURIComponent(partId)}`);
}

/** "6 pcs FAB311161": the pieces of a bundle still on the rack. */
export function bundlePieces(b) {
  return `${(b && (b.on_rack_qty ?? b.qty)) ?? ""} pcs ${(b && b.product_id) || ""}`.trim();
}

/** The numbered steps for a bundle's pieces when crates are in use, worded for its case (Mike 2026-11-04: "an
 *  idiot-proof system the operators will understand"; the crates are Uline mesh crates, the beacons hang on the
 *  BEACON STORAGE RACK until JobQ lights one). {kind, tone, steps, done}:
 *    new      JobQ gave it a NEW crate: the beacon flashing on the storage rack, an empty crate, the pieces, Done;
 *    existing its part's crate is already out (its beacon flashing): the pieces in it, Done;
 *    none     no free beacon was left (or a racked bundle with no crate): an empty crate, then Get a beacon;
 *    done     in its crate (`done` = "In crate BCN-031").
 *  null when crates are off and the bundle has no crate (the rack, as before). */
export function crateSteps(b, cratesOn) {
  const c = openCrate(b);
  const pcs = bundlePieces(b);
  if (!c) {
    if (!cratesOn) return null;
    if (b && b.racked) {
      return { kind: "none", tone: "wait", steps: [
        `Put the ${pcs} and their label in an EMPTY crate`,
        "Press Get a beacon: one starts flashing on the beacon storage rack",
        "Take the flashing beacon and attach it to that crate",
      ] };
    }
    return { kind: "none", tone: "bad", steps: [
      "Take the label from the laser Zebra and stick it on the parts",
      `NO FREE BEACON on the beacon storage rack: put the ${pcs} in an EMPTY crate and set it aside`,
      "Tell the office",
      "When a beacon is back on the rack: press Get a beacon and attach the flashing one to that crate",
    ] };
  }
  if (b && b.racked) return { kind: "done", tone: "ok", steps: [], done: `In crate ${c.beacon_id}` };
  if (c.new) {
    return { kind: "new", tone: "wait", steps: [
      "Take the label from the laser Zebra",
      `Go to the BEACON STORAGE RACK and take the FLASHING beacon ${c.beacon_id}`,
      `Get an EMPTY crate and attach ${c.beacon_id} to it`,
      `Put the ${pcs} in the crate, with the label on the parts`,
      `Press Done: in crate ${c.beacon_id}`,
    ] };
  }
  return { kind: "existing", tone: "ok", steps: [
    "Take the label from the laser Zebra",
    `Find crate ${c.beacon_id}: its beacon is flashing${c.in_crate ? ` (${c.in_crate} pcs in it now)` : ""}`,
    `Put the ${pcs} and the label in it`,
    `Press Done: in crate ${c.beacon_id}`,
  ] };
}

/** The one-line form of `crateSteps` (null as it is): the case's last instruction. */
export function crateHint(b, cratesOn) {
  const s = crateSteps(b, cratesOn);
  if (!s) return null;
  return { tone: s.tone, text: s.done || s.steps[s.steps.length > 1 ? 1 : 0] };
}

/** What a crate action tells the operator. `typed`: the operator typed the beacon (they hold it: no "flashing"). */
export function crateDoneText(out, { typed = false } = {}) {
  if (!out) return "";
  const bid = out.beacon_id || "the crate";
  if (out.already) return `Already in crate ${bid}`;
  let text;
  if (out.released_beacon_id) {
    text = `Done: in crate ${bid}. Put ${out.released_beacon_id} back on the beacon storage rack`;
  } else if (out.new_crate) {
    text = typed
      ? `Done: new crate ${bid}. Attach ${bid} to the crate with the pieces`
      : `Done: new crate ${bid}. Take the FLASHING beacon ${bid} off the beacon storage rack and attach it to the crate with the pieces`;
  } else {
    text = `Done: in crate ${bid}`;
  }
  if (out.label_reprinted) text += `. A new label naming ${bid} is printing at the laser Zebra: put it on the parts`;
  return text;
}

/** Print the bundle's label again on the laser Zebra. Resolves {print_job_id, bundle}. */
export function reprintBundleLabel(bundleId) {
  return post(`/surplus_bundles/${bundleId}/reprint`);
}

/** What the screen says about the bundle's latest label, from the print queue's status. */
export function labelStatusText(status) {
  switch (status) {
    case "printed": return { text: "Label printed", tone: "ok" };
    case "failed": return { text: "Label FAILED to print: reprint it", tone: "bad" };
    case "cancelled": return { text: "Label cancelled: reprint it", tone: "bad" };
    case "pending":
    case "claimed": return { text: "Label printing…", tone: "wait" };
    default: return { text: "No label yet: reprint it", tone: "bad" };
  }
}

/** "6 pcs FAB311160 · Job 10140 · SO-1139" */
export function bundleLine(b) {
  const bits = [`${b.qty} pcs ${b.product_id || ""}`.trim()];
  if (b.master_job_num) bits.push(`Job ${b.master_job_num}`);
  if (b.order_number) bits.push(b.order_number);
  return bits.join(" · ");
}

/** What Complete tells the operator when the laser made surplus (jobq-api answers `surplus_bundles`). "" when none.
 *  Crates in use: each bundle's crate and the numbered steps (`crateSteps`' words). */
export function surplusCompleteNote(made) {
  const list = Array.isArray(made) ? made.filter((m) => m && m.qty > 0) : [];
  if (!list.length) return "";
  const what = (m) => (m.kind === "owed"
    ? `OWED #${m.bundle_id} (NOT surplus, for ${m.owed_order_number || "its order"}): ${m.qty} pcs ${m.product_id || ""}`
    : `Surplus #${m.bundle_id}: ${m.qty} pcs ${m.product_id || ""}`).trimEnd();
  if (list.some((m) => m.crate && m.crate.beacon_id)) {
    const lines = list.map((m) => {
      const c = m.crate;
      if (!c || !c.beacon_id) {
        return `• ${what(m)} → NO FREE BEACON: put the pieces and the label in an EMPTY crate, set it aside and tell the office`;
      }
      if (c.new) {
        return `• ${what(m)} → NEW crate ${c.beacon_id}:\n`
          + `   1. Go to the BEACON STORAGE RACK and take the FLASHING beacon ${c.beacon_id}\n`
          + `   2. Get an EMPTY crate and attach ${c.beacon_id} to it\n`
          + "   3. Put the pieces in it, with the label on the parts";
      }
      return `• ${what(m)} → crate ${c.beacon_id} (its beacon is flashing): put the pieces and the label in it`;
    });
    return "\n\nSURPLUS CRATES: a label is printing at the laser Zebra for:\n" + lines.join("\n")
      + "\nThen press Done on the laser packaging screen.";
  }
  const lines = list.map((m) => `• ${what(m)}`);
  return "\n\nRACK: a label is printing at the laser Zebra for:\n" + lines.join("\n")
    + "\nStick it on the bundle, put it on the SURPLUS RACK, then click Labeled + racked on the laser packaging screen.";
}
